import {CaptionStyleContextMenu} from '@waka/compat/CaptionStyleContextMenu';
import {FrameVideoContextMenu} from '@waka/compat/FrameVideoContextMenu';
import {ImageActionsContextMenu} from '@waka/compat/ImageActionsContextMenu';
import { AudioMergeContextMenu } from '@waka/compat/AudioMergeContextMenu';
import { ClipAudioContextMenu } from '@waka/compat/ClipAudioContextMenu';
import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { MenuItem } from '@crabs-ai/ds-react';
import { copyEditorSelection } from '@waka/compat/clipboard-actions';
import { ContextMenuItem, ContextMenuSeparator } from '../../context-menu';
import {
  FEATURE_BRING_TO_FRONT,
  FEATURE_COPY_LAYERS,
  FEATURE_CUT_LAYERS,
  FEATURE_DUPLICATE_LAYERS,
  FEATURE_SEND_TO_BACK,
} from '../../flags';
import { EditorStarterItem } from '../../items/item-type';
import { bringToFrontOrBack } from '../../state/actions/bring-item-to-front-or-back';
import { duplicateItems } from '../../state/actions/duplicate-items';
import {
  useAllItems,
  useSelectedItems,
  useTracks,
  useWriteContext,
  useCurrentStateAsRef,
} from '../../utils/use-context';
import { silenceReviewTargets } from '../silence-review';
import { getLocalUrls } from '../../caching/load-to-blob-url';
import { getPreparedNativeAssetPath } from '@waka/render/native-prepared-assets';

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);
const modKey = isMac ? '⌘' : 'Ctrl+';

const canRemoveSilence = (item: EditorStarterItem) =>
  item.type === 'video' || item.type === 'audio';

export const TimelineItemContextMenu: React.FC<{
  item: EditorStarterItem;
  onRequestRemoveSilenceDialog?: (itemIds: string[]) => void;
}> = ({ item, onRequestRemoveSilenceDialog }) => {
  const { setState } = useWriteContext();
  const stateRef = useCurrentStateAsRef();
  const { selectedItems } = useSelectedItems();
  const { items: allItems } = useAllItems();
  const { tracks } = useTracks();
  const t = useTranslations();

  const isLocked = useMemo(() => {
    const track = tracks.find((t) => t.items.includes(item.id));
    return track?.locked ?? false;
  }, [tracks, item.id]);

  const isMultiSelection = useMemo(
    () => selectedItems.length > 1 && selectedItems.includes(item.id),
    [selectedItems, item.id],
  );

  const targetItems = useMemo(() => {
    return isMultiSelection
      ? selectedItems
          .map((id) => allItems[id])
          .filter((item): item is EditorStarterItem => item !== undefined)
      : [item];
  }, [isMultiSelection, selectedItems, item, allItems]);

  const itemsRemovableSilence = useMemo(
    () => targetItems.filter(canRemoveSilence),
    [targetItems],
  );
  const hasAnyRemovableWithUrl = silenceReviewTargets(
    stateRef.current, itemsRemovableSilence.map((entry) => entry.id),
    (assetId) => {
      const asset = stateRef.current.undoableState.assets[assetId];
      return asset ? getPreparedNativeAssetPath(asset) ?? getLocalUrls()[assetId] ?? asset.remoteUrl ?? asset.staticPath ?? null : null;
    },
  ).length > 0;

  const handleBringToFront = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setState({
        update: (state) =>
          bringToFrontOrBack({
            state,
            itemId: item.id,
            position: 'front',
          }),
        commitToUndoStack: true,
      });
    },
    [item.id, setState],
  );

  const handleSendToBack = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setState({
        update: (state) =>
          bringToFrontOrBack({
            state,
            itemId: item.id,
            position: 'back',
          }),
        commitToUndoStack: true,
      });
    },
    [item.id, setState],
  );

  const handleCopy = useCallback(
    (e: Event) => {
      e.stopPropagation();
      void copyEditorSelection({
        stateRef,
        setState,
        ids: targetItems.map((item) => item.id),
      });
    },
    [targetItems, stateRef, setState],
  );

  const handleCut = useCallback(
    (e: Event) => {
      e.stopPropagation();
      void copyEditorSelection({
        stateRef,
        setState,
        ids: targetItems.map((item) => item.id),
        cut: true,
      });
    },
    [targetItems, setState, stateRef],
  );

  const handleDuplicate = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setState({
        update: (state) =>
          duplicateItems(
            state,
            targetItems.map((targetItem) => targetItem.id),
          ),
        commitToUndoStack: true,
      });
    },
    [targetItems, setState],
  );

  const handleOpenRemoveSilenceDialog = useCallback(
    (e: Event) => {
      e.stopPropagation();
      onRequestRemoveSilenceDialog?.(itemsRemovableSilence.map((entry) => entry.id));
    },
    [itemsRemovableSilence, onRequestRemoveSilenceDialog],
  );

  const handleContextMenuPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    [],
  );

  return (
    <>
      {FEATURE_CUT_LAYERS ? (
        <ContextMenuItem
          onSelect={handleCut}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.cut')}
            subText={`${modKey}X`}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
      {FEATURE_COPY_LAYERS && (
        <ContextMenuItem
          onSelect={handleCopy}
          onPointerDown={handleContextMenuPointerDown}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.copy')}
            subText={`${modKey}C`}
            className="w-full"
          />
        </ContextMenuItem>
      )}
      {FEATURE_DUPLICATE_LAYERS && (
        <ContextMenuItem
          onSelect={handleDuplicate}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.duplicate')}
            subText={`${modKey}D`}
            className="w-full"
          />
        </ContextMenuItem>
      )}
      {itemsRemovableSilence.length > 0 ? (
        <ContextMenuItem
          onSelect={handleOpenRemoveSilenceDialog}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked || !hasAnyRemovableWithUrl}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.remove-silence')}
            subText={!hasAnyRemovableWithUrl ? '검출 가능한 음성이 없습니다. 배경음악은 무음 제거 대상에서 제외됩니다.' : undefined}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
      {(item.type==='video'||item.type==='image')&&<FrameVideoContextMenu clipId={item.id} sourceType={item.type}/>}
      {item.type==='captions'&&<CaptionStyleContextMenu clipId={item.id}/>}
      {item.type==='image'&&<ImageActionsContextMenu clipId={item.id}/>}
      <ClipAudioContextMenu clipId={item.id} />
      <AudioMergeContextMenu clipId={item.id} />
      <ContextMenuSeparator />
      {FEATURE_BRING_TO_FRONT ? (
        <ContextMenuItem
          onSelect={handleBringToFront}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.bring-to-front')}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
      {FEATURE_SEND_TO_BACK ? (
        <ContextMenuItem
          onSelect={handleSendToBack}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.send-to-back')}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
    </>
  );
};
